/**
 * The public site — what a SIGNED-OUT visitor is shown (the user, 2026-10-14).
 *
 * Two pages share one shape: the master welcome on the platform host, and a
 * rave's own landing page on `<rave>.<domain>` when nobody is signed in (or
 * when a friend's invite link brought them). Both are built from REAL data —
 * a few acts, the map, ride routes, counts — never from invented samples.
 *
 * What is never in here: a person. No dater, no driver, no passenger, no
 * name of anyone but the inviter who chose to share their own link. Dating
 * and rides appear as counts and as routes with no owner.
 *
 * Served by `GET /public/home` (platform host) and `GET /public/rave`
 * (rave host, `?invite=<code>`), both open under the pre-launch gate.
 */

/** App download links. `null` = not published yet — show the install-as-web-app steps instead. */
export interface StoreLinks {
  ios: string | null;
  android: string | null;
  /**
   * True where the link is MyRave's own TEST app (`apple.` → TestFlight,
   * `android.` → the newest test APK) — the button says "join the beta"
   * (dev and staging). False for a real store link — "download the app"
   * (production, once the store links are set).
   */
  beta: { ios: boolean; android: boolean };
}

/** One rave as a card. Only PUBLISHED editions of PUBLIC festivals and editions. */
export interface PublicRaveCard {
  editionId: string;
  festivalName: string;
  editionName: string;
  subdomain: string;
  editionSlug: string;
  /** `https://<subdomain>.<domain>/<editionSlug>` — built by the API from PLATFORM_DOMAIN. */
  url: string;
  timezone: string;
  gatesOpenAt: string | null;
  endsAt: string | null;
  venue: string | null;
  country: string | null;
  logoUrl: string | null;
  coverUrl: string | null;
  counts: { stages: number; acts: number };
  /** True while now is between gatesOpenAt and endsAt. */
  live: boolean;
  /** The rave's "Day 1" (`YYYY-MM-DD`, the edition's `dayOneAnchor`) — the live label's day count. Optional. */
  dayOneAnchor?: string | null;
}

/** A real set, as a sample row. */
export interface SampleAct {
  id: string;
  name: string;
  stage: string;
  /** The category's name ("Music" first, always — `keepMusicFirst`). */
  category: string | null;
  startAt: string;
  endAt: string;
  country: string | null;
  photoUrl: string | null;
}

/** What is on right now, per stage — the live window's taste (shared `liveByStage`). */
export interface SampleLiveStage {
  stage: string;
  now: SampleAct | null;
  next: SampleAct | null;
}

export interface SampleMap {
  imageUrl: string;
  width: number;
  height: number;
  /** Up to 8 real points, in image pixels. */
  pois: { name: string; kind: string | null; x: number; y: number }[];
  /** True when the map is GPS-aligned (control points exist). */
  gps: boolean;
}

export interface SampleRides {
  /** Rides offered on this rave (open ones). */
  offered: number;
  /** Up to 3 real routes — no driver, no passenger. */
  routes: { from: string; to: string; departAt: string | null; seatsLeft: number | null }[];
}

export interface SampleDating {
  /** People with a dating profile on this rave. */
  people: number;
  /** Matches made on this rave. */
  matches: number;
}

/** A rave's real data, cut down to a taste. Every part is null/empty when the rave has none. */
export interface RaveSample {
  rave: PublicRaveCard;
  counts: { stages: number; acts: number; days: number; categories: number };
  /** ~8 real sets from one evening, photos first. */
  lineup: SampleAct[];
  /** Two real sets that overlap, if any. */
  clash: { a: SampleAct; b: SampleAct } | null;
  /** Only when the rave is live now. */
  live: SampleLiveStage[] | null;
  map: SampleMap | null;
  rides: SampleRides | null;
  dating: SampleDating | null;
}

/** How this rave is priced, for the rave page's pricing block. */
export interface PublicRavePrice {
  free: boolean;
  /** The price of this one rave in cents, when not free. */
  cents: number | null;
  currency: string;
}

/**
 * The hero's two lines ("One app for every rave." / "Zero signal required.").
 * Kept in `platform.hero_line`, edited in the admin console, and one is picked
 * at random by the page on every visit. `line2` is drawn in the accent colour.
 * `he1`/`he2` are the Hebrew, null when not written yet (English is shown).
 */
export interface HeroLine {
  id: number;
  line1: string;
  line2: string;
  he1: string | null;
  he2: string | null;
  /**
   * The pair in `lang` — the language the request asked for (`X-MyRave-Locale`,
   * 2026-10-27) — or null when that language has no pair (English is shown).
   * Optional: an older API sends neither.
   */
  text1?: string | null;
  text2?: string | null;
  lang?: string;
}

/**
 * Where the previews fall back to when a page's own rave lacks a screen
 * (the user, 2026-10-15: the rave page is a template driven by data) —
 * console settings (`showcaseMapRave`, `showcaseArtist`) or an automatic rule.
 */
export interface PublicShowcase {
  /** The master page's sample rave: its address and edition name. */
  sample: { url: string; editionName: string } | null;
  /** The rave whose map the map preview shows. */
  map: { url: string; editionName: string } | null;
  /** An act slug of the sample rave for the artist preview; null = the bundle's featured act. */
  artist: string | null;
}

/** `GET /public/home` */
export interface PublicHome {
  stores: StoreLinks;
  /** Every ACTIVE hero line; the page picks one. Empty → the page's built-in pair. */
  heroLines: HeroLine[];
  /** Raves running right now ("join the rave live now"). */
  live: PublicRaveCard[];
  /** Upcoming raves, soonest first (not the live ones). Up to 9. */
  upcoming: PublicRaveCard[];
  /** The master page's real sample — a live rave first, else the next one with a line-up. */
  sample: RaveSample | null;
  /** Where previews fall back to. Older servers send none. */
  showcase?: PublicShowcase;
}

/** `GET /public/people` — the "people using MyRave" line at the foot of every page. */
export interface PublicPeople {
  people: number;
}

/** `GET /public/rave?invite=<code>` on a rave host. */
export interface PublicRave {
  stores: StoreLinks;
  heroLines: HeroLine[];
  sample: RaveSample;
  description: string | null;
  price: PublicRavePrice;
  /** Present only for a valid invite code of THIS rave. */
  invite: { inviterName: string } | null;
  /**
   * The act slug of this rave's artist whose screen has something to show (a
   * photo and a story or links); null when it has none — the page then borrows
   * the showcase's artist rather than show an empty artist sheet.
   */
  featuredArtist?: string | null;
  /** Where previews fall back to when this rave lacks a screen. Older servers send none. */
  showcase?: PublicShowcase;
}

/** `POST /editions/:id/invite` (signed in) — the person's own link for this rave. */
export interface RaveInviteLink {
  code: string;
  /** The rave URL with `?invite=<code>`. */
  url: string;
}

/**
 * `POST /public/invites` (step 89, the user, 2026-10-29) — the invite "Invite a friend" shows as
 * a QR code and shares: this rave's (`editionId`), or MyRave's (none); the account's, or —
 * signed out — this device's (`guest`). The same code every time it is asked again.
 */
export interface InviteLink extends RaveInviteLink {
  kind: 'rave' | 'myrave';
  guest: boolean;
  /** The rave's name for a rave invite — the screen's words, offline too. */
  raveName: string | null;
  /** The inviter's name as the friend will see it; null for a guest. */
  inviterName: string | null;
}

/** `GET /public/invites/:code` — who invited, to what: a name and a rave's name, nothing more. */
export interface InviteFromAnswer {
  inviterName: string;
  /** Null: an invite to MyRave itself. */
  editionId: string | null;
  editionName: string | null;
}

/** How a link was shared (step 89): the QR code, the phone's share sheet, WhatsApp, a copied link. */
export const INVITE_VIAS = ['qr', 'share', 'wa', 'copy'] as const;
export type InviteVia = (typeof INVITE_VIAS)[number];

/** The query parameter that says how the link was shared. */
export const INVITE_VIA_PARAM = 'via';

export const isInviteVia = (v: unknown): v is InviteVia => typeof v === 'string' && (INVITE_VIAS as readonly string[]).includes(v);

/** The way in `?via=`, read as loosely as `inviteCodeIn` reads a code (a share sheet's text glued on). */
export function inviteViaIn(raw: unknown): InviteVia | null {
  if (typeof raw !== 'string') return null;
  const m = /^\s*([a-z]+)(?![a-z0-9])/i.exec(raw);
  const via = m?.[1]?.toLowerCase();
  return isInviteVia(via) ? via : null;
}

/** An invite link, saying how it was shared. Pure string work: `shared/` has no DOM. */
export function withInviteVia(url: string, via: InviteVia): string {
  try {
    const u = new URL(url);
    u.searchParams.set(INVITE_VIA_PARAM, via);
    return u.toString();
  } catch {
    return url;
  }
}

/** The query parameter an invite link carries. */
export const INVITE_PARAM = 'invite';

/**
 * Where a MyRave invite (step 89 — no rave) lands on the main domain: `<platform>/app/invite?invite=<code>`.
 * TWO parts on purpose (the user, 2026-10-29: "invite a friend from the main app opened the invite out of
 * the app in Chrome … and not the app"): the Android app claims only the main domain's two-part addresses
 * (`mobile/app.config.ts`, `pathPattern: '/.*\/.*'`), and the bare `<platform>/?invite=` was one part, so
 * Android left it to the browser. The installed app's shell opens it as `app.<platform>/invite?…`; a
 * browser shows the front page. `app` is a reserved subdomain — never a rave's `/<rave>/<edition>`.
 */
export const MYRAVE_INVITE_PATH = '/app/invite';

/** An invite code: 10 characters, URL-safe, no look-alikes. */
export const isInviteCode = (v: unknown): v is string => typeof v === 'string' && /^[a-hjkmnp-z2-9]{10}$/.test(v);

/**
 * The invite code in whatever arrived as `?invite=` (the user, 2026-10-15): a
 * share sheet glued the message onto the link — "g3cyv6kqgv Come to Imagine
 * 22 with me…" — and the whole of it came back as the parameter. The code is
 * the first 10-character word of its alphabet at the start; anything after a
 * space or punctuation is the message, not the code.
 */
export function inviteCodeIn(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const m = /^\s*([a-hjkmnp-z2-9]{10})(?![a-z0-9])/i.exec(raw);
  const code = m?.[1]?.toLowerCase();
  return isInviteCode(code) ? code : null;
}
